import React from 'react';

import { Box, Typography } from '@mui/material';

import { FlexRow } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { ITask } from '../../tasks/interfaces/ITask';
import { MibCodeDetails } from '../../tasks/types/mibCodeDetails';
import styles from '../styles.module.scss';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { TaskDetailsItem } from './components/TaskDetailsItem';
import { TaskNote } from './components/TaskNote';

export const MIBCodeDetailsDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  deleteHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const data: MibCodeDetails = task.taskData as MibCodeDetails;
  const evidence = task.evidence;
  const evidenceReasons: string[] = (data?.reason || []).map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  return (
    <Box data-label={ElementLocators.LOCATOR_MIB_CODE_DETAILS_SECTION}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
        mb={0.75}
        id={task.id.toString()}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={0.5}>
            <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
          </Box>
          <Typography variant="h2">
            {Resource.TASK_TITLE_MIB_CODE_DETAILS}
          </Typography>
        </Box>
        <Box>
          <Typography color="textSecondary" variant="subtitle2">
            {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
          </Typography>
        </Box>
      </Box>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      <TaskDetailsItem
        leftItem={Resource.MIB_CODE}
        rightItem={data?.code || Resource.NA}
      />

      {evidenceExists(evidence) && evidence && (
        <FileDetail
          documents={evidence.documents}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}

      {task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED && (
          <FileUpload
            leftItem={evidenceExists(evidence) ? '' : Resource.FILES}
            selectedFiles={onFileSelection}
            type={DocumentType.MIB_CODE_DETAILS}
            ongoingUpload={task.ongoingUpload?.[DocumentType.MIB_CODE_DETAILS]}
          />
        )}

      <TaskNote taskID={task.id} originalNote={data?.note} />

      {(evidenceReasons?.length > 0 || data?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {data?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={data.reasonDescription}
            />
          )}
        </>
      )}
    </Box>
  );
};
